import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration } from '../../../data/configuration.js';
import { CameraController, CameraViews } from '../../../camera-controller.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import { getBodyColourImageFilePath, getFilePathForGlossPaintColourId, getFilePathForMattePaintColourId } from '../../paintwork-view.tsx';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import ViewContentRows from '../../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface DashboardViewProps {
	interiorConfigurator: InteriorConfigurator;
	cameraController: CameraController;
	onBackButtonClicked: () => void;
}

interface DashboardViewContextType {
	selectedDashboardItemId: string;
	handleDashboardItemClicked: (id: string) => void;
	selectedSteeringWheelItemId: string;
	handleSteeringWheelItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function DashboardView(props: DashboardViewProps) {

	// initial values.
	const initialDashboardItemId = Configuration.dashboardOption;
	const initialDashboardItemName = () => getItemName(initialDashboardItemId);
	const initialSteeringWheelItemId = Configuration.steeringWheelOption;
	const initialSteeringWheelItemName = () => getSteeringWheelItemName(initialSteeringWheelItemId);
	
	// states.
	const [selectedDashboardItemId, setSelectedDashboardItemId] = useState(initialDashboardItemId);
	const [selectedDashboardItemName, setSelectedDashboardItemName] = useState(initialDashboardItemName);
	const [selectedSteeringWheelItemId, setSelectedSteeringWheelItemId] = useState(initialSteeringWheelItemId);
	const [selectedSteeringWheelItemName, setSelectedSteeringWheelItemName] = useState(initialSteeringWheelItemName);
	
	// handlers.

	const handleDashboardItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedDashboardItemId(id);
		setSelectedDashboardItemName(getItemName(id));

		// update the configuration.
		Configuration.dashboardOption = id;
		Configuration.updateCosts();

		// select the dashboard option on the car.
		props.interiorConfigurator.selectDashboardOption(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.DashboardView);
	};

	const handleSteeringWheelItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedSteeringWheelItemId(id);
		setSelectedSteeringWheelItemName(getSteeringWheelItemName(id));

		// update the configuration.
		Configuration.steeringWheelOption = id;
		Configuration.updateCosts();

		// select the steering wheel option on the car.
		props.interiorConfigurator.selectSteeringWheelOption(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.SteeringWheelView);
	};

	// return the HTML.
	return (
		<DashboardViewContext.Provider value={{ selectedDashboardItemId, handleDashboardItemClicked, selectedSteeringWheelItemId, handleSteeringWheelItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-dashboard">DASHBOARD</h2>
					</div>

					<SectionPanel
						subHeading={'FASCIA'}
						modalContent={CloudData.getModalContent(selectedDashboardItemId, CloudData.dashboardOptions)}
					>
						<SelectedSectionItem
							itemName={selectedDashboardItemName}
							sectionPrice={CloudData.getPrice(selectedDashboardItemId, CloudData.dashboardOptions, { ref: false })}
						/>
						<DashboardItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'STEERING WHEEL'}
						modalContent={CloudData.getModalContent(selectedSteeringWheelItemId, CloudData.steeringWheelOptions)}
					>
						<SelectedSectionItem
							itemName={selectedSteeringWheelItemName}
							sectionPrice={CloudData.getPrice(selectedSteeringWheelItemId, CloudData.steeringWheelOptions, { ref: false })}
						/>
						<SteeringWheelItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</DashboardViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const DashboardViewContext = createContext<DashboardViewContextType | undefined>(undefined);

function DashboardItems() {
	return (
		<ViewContentRows>
			<DashboardItem id={'D024'} imageFilePath={'matte-silver'} />
			<DashboardItem id={'D026'} imageFilePath={'gloss-black'} />
			<DashboardItem id={'D001'} imageFilePath={'body-coloured-dynamically-generated'} />
			<DashboardItem id={'D027'} imageFilePath={'matte-body-coloured-dynamically-generated'} />
		</ViewContentRows>
	);
}

function DashboardItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(DashboardViewContext);
	if (!context) {
		throw new Error('DashboardViewContext must be used within a DashboardView component');
	}

	// if the 'gloss body coloured' or 'matte body coloured' options are chosen, get the image for the relevant body paint instead of the given path.
	let imageFilePath = `assets/images/configuration-items/interior/dashboard/${props.imageFilePath}.webp`;
	if (props.id === Configuration.dashboardMatchingGlossBodyColourId) {
		imageFilePath = getBodyColourImageFilePath(getFilePathForGlossPaintColourId);
	}
	else if (props.id === Configuration.dashboardMatchingMatteBodyColourId) {
		imageFilePath = getBodyColourImageFilePath(getFilePathForMattePaintColourId);
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={imageFilePath}
			isSelected={props.id === context.selectedDashboardItemId}
			onItemClicked={context.handleDashboardItemClicked}
		/>
	);
}

function SteeringWheelItems() {
	return (
		<ViewContentRows>
			<SteeringWheelItem id={'SWC001'} imageFilePath={'satin'} />
			<SteeringWheelItem id={'SWC003'} imageFilePath={'midsummer'} />
		</ViewContentRows>
	);
}

function SteeringWheelItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(DashboardViewContext);
	if (!context) {
		throw new Error('DashboardViewContext must be used within a DashboardView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getSteeringWheelItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/steering-wheel/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedSteeringWheelItemId}
			onItemClicked={context.handleSteeringWheelItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.dashboardOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getSteeringWheelItemName(id: string): string {
	return CloudData.getName(id, CloudData.steeringWheelOptions);
}
